import { graphql } from 'gatsby'
import React from 'react'
import StoryCarousel, {
  StoryCarouselCardEntry,
  StoryCarouselProps,
  StoryCarouselYearEntry,
} from 'src/components/organisms/brandEducation/storyCarousel'
import { PrismicPageDataBodyStoryCarousel } from 'src/typings/generated/graphql'
import { naturalRange } from 'src/utils/naturalRange'
import { NaturalLowerOrEqualThan } from 'src/utils/types/NaturalLowerOrEqualThan'

/**
 * Maximum number of years that can be displayed in the `StoryCarousel`.
 */
const YEARS_PER_CAROUSEL = 3

/**
 * Maximum number of cards that can be displayed for each year in the `StoryCarousel` in wide viewports.
 *
 * In narrow viewports, only one card per year is displayed.
 */
const CARDS_PER_YEAR = 4

/**
 * Extracts a card entry for a specific year and card index from the given `StoryCarousel` slice data.
 *
 * @param slice the StoryCarousel slice data from Prismic
 * @param yearIndex the index of the year
 * @param cardIndex the index of the card within the year
 * @returns the extracted `StoryCarouselCardEntry` if all required fields are present, or `undefined` if any required field is missing
 */
const extractCardEntryByYearAndCardIndices = (
  slice: PrismicPageDataBodyStoryCarousel,
  yearIndex: NaturalLowerOrEqualThan<typeof YEARS_PER_CAROUSEL>,
  cardIndex: NaturalLowerOrEqualThan<typeof CARDS_PER_YEAR>
): StoryCarouselCardEntry | undefined => {
  const cardImageKey = `year_${yearIndex}_card_${cardIndex}_image` as const
  const cardTitleKey = `year_${yearIndex}_card_${cardIndex}_title` as const
  const cardBodyKey = `year_${yearIndex}_card_${cardIndex}_body` as const

  const cardImage = slice.primary[cardImageKey]
  const cardTitle = slice.primary[cardTitleKey]
  const cardBody = slice.primary[cardBodyKey]

  // Skip the card entry if any of the required fields (image, title, or body) are missing
  if (
    !cardImage?.gatsbyImageData ||
    !cardTitle ||
    !cardBody?.richText?.length
  ) {
    return undefined
  }

  return {
    title: cardTitle,
    body: cardBody,
    image: cardImage.gatsbyImageData,
    imageAlt: cardImage.alt ?? '',
  }
}

/**
 * Extracts card entries for a specific year index from the given `StoryCarousel` slice data.
 *
 * A card entry is only included if all required fields (image, title, and body) are present. Card entries are sorted
 * by their index in the slice data, and any missing or incomplete card entries are skipped.
 *
 * @param slice the StoryCarousel slice data from Prismic
 * @param yearIndex the index of the year for which to extract card entries
 * @returns an array of extracted `StoryCarouselCardEntry` objects for the specified year
 */
const extractCardEntriesByYearIndex = (
  slice: PrismicPageDataBodyStoryCarousel,
  yearIndex: NaturalLowerOrEqualThan<typeof YEARS_PER_CAROUSEL>
): StoryCarouselCardEntry[] => {
  const cardEntries: StoryCarouselCardEntry[] = []
  const cardIndices = naturalRange(CARDS_PER_YEAR)

  // Iterate over the card indices for the current year and extract the card entries
  cardIndices.forEach(cardIndex => {
    const cardEntry = extractCardEntryByYearAndCardIndices(
      slice,
      yearIndex,
      cardIndex
    )

    if (cardEntry) {
      cardEntries.push(cardEntry)
    }
  })

  return cardEntries
}

/**
 * Extracts year entries from the given `StoryCarousel` slice data.
 *
 * Invalid or incomplete year entries (i.e., years without any valid card entries) are skipped.
 *
 * @param slice the StoryCarousel slice data from Prismic
 * @returns an array of extracted `StoryCarouselYearEntry` objects
 */
const extractYearEntries = (
  slice: PrismicPageDataBodyStoryCarousel
): StoryCarouselYearEntry[] => {
  const yearEntries: StoryCarouselYearEntry[] = []
  const yearIndices = naturalRange(YEARS_PER_CAROUSEL)

  // Iterate over the year indices and extract the year and its corresponding card entries
  yearIndices.forEach(yearIndex => {
    const yearKey = `year_${yearIndex}` as const
    // Extract the year as a string (first 4 characters)
    const year = slice.primary[yearKey]?.toString().slice(0, 4)

    // Only proceed if the year is defined and not empty
    if (year) {
      const cardEntries = extractCardEntriesByYearIndex(slice, yearIndex)

      // Only add the year entry if there are valid card entries for that year
      if (cardEntries.length > 0) {
        yearEntries.push({
          year,
          cardEntries,
        })
      }
    }
  })

  return yearEntries
}

/**
 * Props for the `StoryCarouselSlice` component.
 */
type Props = {
  slice: PrismicPageDataBodyStoryCarousel
}

/**
 * Renders the `StoryCarousel` component based on the provided Prismic slice data.
 */
const StoryCarouselSlice = ({ slice }: Props) => {
  const yearEntries = extractYearEntries(slice)

  const props: StoryCarouselProps = {
    anchor: slice.primary.anchor ?? undefined,
    headline: slice.primary.headline ?? undefined,
    yearEntries,
  }

  return <StoryCarousel {...props} />
}

export default StoryCarouselSlice

export const StoryCarouselFragment = graphql`
  fragment PageDataBodyStoryCarousel on PrismicPageDataBodyStoryCarousel {
    primary {
      anchor
      headline
      year_1
      year_1_card_1_image {
        gatsbyImageData
        alt
      }
      year_1_card_1_title
      year_1_card_1_body {
        richText
      }
      year_1_card_2_image {
        gatsbyImageData
        alt
      }
      year_1_card_2_title
      year_1_card_2_body {
        richText
      }
      year_1_card_3_image {
        gatsbyImageData
        alt
      }
      year_1_card_3_title
      year_1_card_3_body {
        richText
      }
      year_1_card_4_image {
        gatsbyImageData
        alt
      }
      year_1_card_4_title
      year_1_card_4_body {
        richText
      }
      year_2
      year_2_card_1_image {
        gatsbyImageData
        alt
      }
      year_2_card_1_title
      year_2_card_1_body {
        richText
      }
      year_2_card_2_image {
        gatsbyImageData
        alt
      }
      year_2_card_2_title
      year_2_card_2_body {
        richText
      }
      year_2_card_3_image {
        gatsbyImageData
        alt
      }
      year_2_card_3_title
      year_2_card_3_body {
        richText
      }
      year_2_card_4_image {
        gatsbyImageData
        alt
      }
      year_2_card_4_title
      year_2_card_4_body {
        richText
      }
      year_3
      year_3_card_1_image {
        gatsbyImageData
        alt
      }
      year_3_card_1_title
      year_3_card_1_body {
        richText
      }
      year_3_card_2_image {
        gatsbyImageData
        alt
      }
      year_3_card_2_title
      year_3_card_2_body {
        richText
      }
      year_3_card_3_image {
        gatsbyImageData
        alt
      }
      year_3_card_3_title
      year_3_card_3_body {
        richText
      }
      year_3_card_4_image {
        gatsbyImageData
        alt
      }
      year_3_card_4_title
      year_3_card_4_body {
        richText
      }
    }
  }
`
